import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
} from '@mui/material';

import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import barcodeApi from '../services/barcodeApi';

/** Размер страницы при выгрузке записей журнала с сервера */
const PAGE_LIMIT = 500;

/** Дата в формате input[type="date"] (yyyy-mm-dd) */
const toDateInputValue = (date) => date.toISOString().split('T')[0];

/** Интервал по умолчанию - последние 7 дней */
const getDefaultDateFrom = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return toDateInputValue(date);
};

const AdminSmsChecksPage = () => {
  /** Загруженные записи журнала (поиск/сортировка/фильтры - на клиенте) */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /** Временной интервал загрузки */
  const [filterDateFrom, setFilterDateFrom] = useState(getDefaultDateFrom());
  const [filterDateTo, setFilterDateTo] = useState(toDateInputValue(new Date()));

  /** Уведомления */
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /**
   * Загрузка всех записей журнала за интервал (выгружаем по страницам)
   * @param {string} dateFrom - дата начала (yyyy-mm-dd)
   * @param {string} dateTo - дата окончания (yyyy-mm-dd)
   * @returns {Promise<void>}
   */
  const fetchRows = useCallback(async (dateFrom, dateTo) => {
    try {
      setLoading(true);
      const all = [];
      let page = 1;
      let total = Infinity;

      while (all.length < total) {
        const payload = await barcodeApi.getSmsChecks({
          page,
          limit: PAGE_LIMIT,
          date_from: dateFrom,
          date_to: dateTo,
        });
        const items = payload.data || [];
        total = payload.count || items.length;
        if (!items.length) break;
        all.push(...items);
        if (items.length < PAGE_LIMIT) break;
        if (total > PAGE_LIMIT) {
          setSnackbar({ open: true, message: `Загрузка записей: ${all.length.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}...`, severity: 'info' });
        }
        page++;
      }

      setRows(all);
      setSnackbar({ open: true, message: `Загружено записей: ${all.length.toLocaleString('ru-RU')}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Не удалось загрузить данные: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(filterDateFrom, filterDateTo);
    /** Загружаем интервал по умолчанию при монтировании */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => {
    fetchRows(filterDateFrom, filterDateTo);
  };

  /** --- Выгрузка CSV --- */

  /** Экранирование значения для CSV (разделитель ';' + BOM - корректно открывается в Excel с кириллицей) */
  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    return `"${String(value).replace(/"/g, '""')}"`;
  };

  /** Плоское текстовое представление значений колонок (для CSV и печати) */
  const textExtractors = {
    datetime: (value) => value ? new Date(value).toLocaleString('ru-RU') : '',
    type: (value) => value === 'inbox' ? 'Входящее' : 'Исходящее',
    statusLabel: (value) => value || '—',
    region: (value) => value || '—',
    relatedSmsId: (value) => value ?? '—',
  };

  const getTextValue = (columnName, value) => {
    const extractor = textExtractors[columnName];
    return extractor ? extractor(value) : (value ?? '');
  };

  /**
   * Кастомная выгрузка CSV: все строки с учётом текущих поиска/фильтров/сортировки таблицы.
   * Возврат false отменяет стандартную выгрузку mui-datatables.
   */
  const handleCsvDownload = (buildHead, buildBody, columnsMeta, displayData) => {
    /** Учитываем только видимые колонки (панель "Просмотр столбцов") */
    const visibleColumns = columnsMeta
      .map((column, index) => ({ ...column, index }))
      .filter((column) => column.display !== 'false');

    const header = visibleColumns.map((column) => csvEscape(column.label)).join(';');
    const lines = displayData.map((row) =>
      visibleColumns.map((column) => csvEscape(getTextValue(column.name, row.data[column.index]))).join(';')
    );

    const csv = String.fromCharCode(0xFEFF) + [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Журнал СМС.${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: `Выгружено записей: ${displayData.length.toLocaleString('ru-RU')}`, severity: 'success' });
    return false;
  };

  /** --- Таблица --- */

  const columns = [
    {
      name: "datetime",
      label: "Дата и время",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ? new Date(value).toLocaleString('ru-RU') : '',
        customBodyRenderLite: (dataIndex) => getTextValue('datetime', rows[dataIndex]?.datetime),
      }
    },
    {
      name: "type",
      label: "Тип",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            label={value === 'inbox' ? 'Входящее' : 'Исходящее'}
            color={value === 'inbox' ? 'primary' : 'default'}
            size="small"
            variant="outlined"
          />),
        customBodyRenderLite: (dataIndex) => getTextValue('type', rows[dataIndex]?.type),
      }
    },
    {
      name: "phone",
      label: "Телефон",
      options: { filter: false, sort: true }
    },
    {
      name: "text",
      label: "Текст",
      options: { filter: false, sort: true }
    },
    {
      name: "provider",
      label: "Оператор",
      options: { filter: true, sort: true }
    },
    {
      name: "statusLabel",
      label: "Статус",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const row = rows[tableMeta.rowIndex] || {};
          const color = row.type === 'inbox'
            ? (value === 'Подлинный' ? 'success' : value === 'Повторный' ? 'warning' : 'default')
            : (value === 'delivered' || value === 'Delivered' ? 'success' : 'default');
          return <Chip label={value || '—'} color={color} size="small" variant="outlined" />;
        },
        customBodyRenderLite: (dataIndex) => getTextValue('statusLabel', rows[dataIndex]?.statusLabel),
      }
    },
    {
      name: "region",
      label: "Регион",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || '—',
        customBodyRenderLite: (dataIndex) => getTextValue('region', rows[dataIndex]?.region),
      }
    },
    {
      name: "relatedSmsId",
      label: "Связанное СМС",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ?? '—',
        customBodyRenderLite: (dataIndex) => getTextValue('relatedSmsId', rows[dataIndex]?.relatedSmsId),
      }
    },
  ];

  const options = {
    storageKey: 'smsChecksTable',
    jumpToPage: true,
    selectableRows: 'none',
    rowsPerPage: 50,
    rowsPerPageOptions: [25, 50, 100],
    filterType: 'multiselect',
    sortOrder: { name: 'datetime', direction: 'desc' },
    onDownload: handleCsvDownload,
    textLabels: {
      body: {
        noMatch: loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : 'Записи не найдены',
        toolTip: "Сортировка",
        columnHeaderTooltip: (column) => `Сортировка для ${column.label}`
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
        jumpToPage: "Перейти на страницу:",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
      filter: {
        all: "Все",
        title: "ФИЛЬТРЫ",
        reset: "СБРОС",
      },
      viewColumns: {
        title: "Показать столбцы",
        titleAria: "Показать/скрыть столбцы таблицы",
      },
    },
  };

  const appTheme = useTheme();
  const getMuiTheme = () => createTheme(appTheme, {
    components: {
      MUIDataTableToolbar: {
        styleOverrides: {
          filterPaper: {
            width: '25%',
            minWidth: '20vw',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            flexBasis: '100% !important',
            maxWidth: '100% !important',
            width: '100%',
            marginLeft: '0',
          },
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '1rem',
            minWidth: '20vw !important',
            padding: '0.5rem !important',
          }
        }
      },
      MUIDataTableViewCol: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '0.5rem !important',
          }
        }
      },
    },
  });

  return (
    <>
      {/** Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Проверки по СМС
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Журнал входящих и исходящих СМС проверки подлинности товаров
          </Typography>
        </Box>
      </Box>

      {/** Выбор временного интервала загрузки */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            type="date"
            label="С даты"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            type="date"
            label="По дату"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Загрузить'}
          </Button>
        </Grid>
      </Grid>

      {/** Таблица журнала */}
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title={`Журнал СМС (${rows.length.toLocaleString('ru-RU')})`}
          data={rows}
          columns={columns}
          options={options}
        />
      </ThemeProvider>

      {/** Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminSmsChecksPage;
